import type { InferInsertModel, InferSelectModel } from 'drizzle-orm';
import { relations } from 'drizzle-orm';
import {
  type AnyPgColumn,
  boolean,
  pgTable,
  text,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core';

import { softDelete } from '../columns/soft-delete';
import { bataillonTasks } from './bataillon-tasks';
import { soldiers } from './soldiers';
import { users } from './users';

/**
 * Batalhão (módulo "Batalhões" — prints 01–06 do `export-batalhoes/admin`).
 *
 * NOVO em relação ao schema atual (só tinha name/isActive):
 *  - tag            sigla de 2–4 letras exibida no selo (LOG, INF, ENG…)
 *  - majorId        Major dos Jovens → users (role major/general/marechal)
 *  - kidsMajorId    Major do Kids → users (perfil major)
 *  - kidsCaptainId  Capitão Kids responsável → users (perfil capitão)
 *  - youthCaptainId Capitão Jovens responsável → users (perfil capitão)
 *  - description    HTML do TipTap, sanitizado no service (allowlist)
 *
 * `soldiers` importa `bataillons` (bataillonId). Para não criar ciclo de import em tempo de
 * avaliação, as FKs para soldiers usam `(): AnyPgColumn => soldiers.id` (padrão do Drizzle).
 */
export const bataillons = pgTable('bataillons', {
  id: uuid('id')
    .primaryKey()
    .$defaultFn(() => Bun.randomUUIDv7()),
  name: text('name').notNull(),
  tag: text('tag'),
  /** Major responsável pelos Jovens (a coluna já existia como "major"). */
  majorId: uuid('major_id').references((): AnyPgColumn => users.id, {
    onDelete: 'set null',
  }),
  /** Major responsável pelo Kids. */
  kidsMajorId: uuid('kids_major_id').references((): AnyPgColumn => users.id, {
    onDelete: 'set null',
  }),
  kidsCaptainId: uuid('kids_captain_id').references((): AnyPgColumn => users.id, {
    onDelete: 'set null',
  }),
  youthCaptainId: uuid('youth_captain_id').references((): AnyPgColumn => users.id, {
    onDelete: 'set null',
  }),
  description: text('description'),
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at')
    .notNull()
    .$defaultFn(() => new Date()),
  updatedAt: timestamp('updated_at')
    .$onUpdate(() => new Date())
    .notNull(),
  ...softDelete,
});

export const bataillonsRelations = relations(bataillons, ({ one, many }) => ({
  major: one(users, {
    fields: [bataillons.majorId],
    references: [users.id],
    relationName: 'major',
  }),
  kidsMajor: one(users, {
    fields: [bataillons.kidsMajorId],
    references: [users.id],
    relationName: 'kidsMajor',
  }),
  kidsCaptain: one(users, {
    fields: [bataillons.kidsCaptainId],
    references: [users.id],
    relationName: 'kidsCaptain',
  }),
  youthCaptain: one(users, {
    fields: [bataillons.youthCaptainId],
    references: [users.id],
    relationName: 'youthCaptain',
  }),
  tasks: many(bataillonTasks),
  soldiers: many(soldiers),
}));

export type Bataillon = InferSelectModel<typeof bataillons>;
export type CreateBataillonInput = InferInsertModel<typeof bataillons>;
export type UpdateBataillonInput = Partial<CreateBataillonInput>;
